import {
  GetLabelsOptions,
  GetLabelsWithKrnOptions,
  IActionsCreator,
} from "types/action";
import { CALL_API } from "../middleware/api";
import {
  GET_LABELS_REQUEST,
  GET_LABELS_SUCCESS,
  GET_LABELS_FAILURE,
} from "./labelsActions";
import {
  GET_LABELS_WITH_KRN_REQUEST,
  GET_LABELS_WITH_KRN_SUCCESS,
  GET_LABELS_WITH_KRN_FAILURE,
} from "./labelsWithKrnActions";

type LabelsFetchParams = {
  domain: string;
  clientId: string;
  realm: string;
  language: string;
  instanceId: string;
  payableTypeDefinitions: { typeKrn: string }[];
  payableUi: string;
};

export const LabelsActionsCreator = {
  getLabels: (options: GetLabelsOptions): IActionsCreator => ({
    [CALL_API]: {
      types: [GET_LABELS_REQUEST, GET_LABELS_SUCCESS, GET_LABELS_FAILURE],
      request: () =>
        fetchLabels(options),
      successHandler: options.successHandler,
      failureHandler: options.failureHandler,
    },
  }),

  // Get labels with KRN (without merging the results into one object)

  getLabelsWithKrn: (options: GetLabelsWithKrnOptions): IActionsCreator => ({
    [CALL_API]: {
      types: [
        GET_LABELS_WITH_KRN_REQUEST,
        GET_LABELS_WITH_KRN_SUCCESS,
        GET_LABELS_WITH_KRN_FAILURE,
      ],
      request: () =>
        fetchLabelsWithKrn(options),
      successHandler: options.successHandler,
      failureHandler: options.failureHandler,
    },
  }),
};

const fetchLabelsBaseCache = new Map<
  string,
  Promise<{ merged: any; byKrn: Record<string, any> }>
>();

function getLabelsBaseCacheKey({
  domain,
  clientId,
  realm,
  language,
  instanceId,
  payableTypeDefinitions,
  payableUi,
}: LabelsFetchParams): string {
  const krns = payableTypeDefinitions.map((item) => item.typeKrn).join(",");
  return [domain, clientId, realm, language, instanceId, krns, payableUi].join("|");
}

const fetchLabelsBase = async ({
  domain,
  clientId,
  realm,
  language,
  instanceId,
  payableTypeDefinitions,
  payableUi,
}: LabelsFetchParams): Promise<{ merged: any; byKrn: Record<string, any> }> => {
  const cacheKey = getLabelsBaseCacheKey({
    domain,
    clientId,
    realm,
    language,
    instanceId,
    payableTypeDefinitions,
    payableUi,
  });

  if (fetchLabelsBaseCache.has(cacheKey)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return fetchLabelsBaseCache.get(cacheKey)!;
  }

  const promise = (async () => {
    const typeKrns = payableTypeDefinitions?.map((item) =>
      item.typeKrn.replace(/:/g, ".")
    );

    if (typeKrns?.length > 0 && payableUi?.toLowerCase() === "true") {
      const krnFetchUrls = typeKrns.map(
        (typeKrn) =>
          `https://${domain}/label-management/${clientId}/${realm}/${typeKrn}.web.${getFileLanguageFormat(
            language
          )}.json`
      );

      const results = await Promise.allSettled(
        krnFetchUrls.map(async (url, idx) => {
          try {
            const response = await fetch(url);
            if (response.ok) {
              const json = await response.json();
              return { key: typeKrns[idx], value: json };
            }
          } catch {
            // Silently handle the error without logging to the console
          }
          // If fetch fails or response not ok, return null value
          return { key: typeKrns[idx], value: null };
        })
      );

      const validResults = results
        .filter(
          (
            res
          ): res is PromiseFulfilledResult<{ key: string; value: Record<string, any> | null }> =>
            res.status === "fulfilled" && res.value.value != null
        )
        .map((res) => res.value);

      // Merged object
      const merged = validResults.reduce(
        (acc, curr) => ({ ...acc, ...curr.value }),
        {}
      );

      // KRN-keyed object
      const byKrn: Record<string, any> = {};
      validResults.forEach(({ key, value }) => {
        byKrn[key] = value;
      });

      return { merged, byKrn };
    }

    return { merged: {}, byKrn: {} };
  })();

  fetchLabelsBaseCache.set(cacheKey, promise);
  return promise;
};

const fetchLabels = async (
  options: GetLabelsOptions
): Promise<Record<string, any>> => {
  const { domain, clientId, realm, language, instanceId } = options;
  const { merged } = await fetchLabelsBase(options);
  if (Object.keys(merged).length > 0) return merged;

  // If not payable ui, Get Legacy Clientoverrides

  const fetchUrls = [
    `https://${domain}/label-management/${clientId}/${realm}/clientoverride_web_${language}.json`,
    `https://${domain}/label-management/${clientId}/${realm}/ezpay6_web_${language}.json`,
    `https://${domain}/label-management/${clientId}/${realm}/${instanceId}_web_${language}.json`,
  ];

  for (const fetchUrl of fetchUrls) {
    try {
      const response = await fetch(fetchUrl);
      if (response.ok) {
        const jsonData = await response.json();
        if (jsonData) {
          return jsonData;
        }
      }
    } catch (error) {
      // Silently handle the error without logging to the console
    }
  }

  throw new Error("No valid data found from any URL");
};

const fetchLabelsWithKrn = async (
  options: GetLabelsWithKrnOptions
) => {
  const { byKrn } = await fetchLabelsBase(options);

  const nonNullByKrn = Object.fromEntries(
    Object.entries(byKrn).filter(([, value]) => value != null)
  );

  if (Object.keys(nonNullByKrn).length > 0) {
    return nonNullByKrn;
  }
  throw new Error("No valid data found from any URL");
};

function getFileLanguageFormat(language: string): string {
  switch (language) {
    case "en":
      return "en.us";
    case "es":
      return "es.us";
    case "fr":
      return "fr.ca";
    default:
      return "en.us"; // Default to English if no match
  }
}

export default LabelsActionsCreator;
